import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { Loader2, RefreshCw, Download, Play, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGES = {
  queued:                       { label: "Queued",     cls: "bg-slate-100 text-slate-700" },
  running:                      { label: "Running…",   cls: "bg-amber-100 text-amber-700" },
  ready:                        { label: "Ready",      cls: "bg-emerald-100 text-emerald-700" },
  failed:                       { label: "Failed",     cls: "bg-rose-100 text-rose-700" },
  failed_timeout:               { label: "Timed out",  cls: "bg-rose-100 text-rose-700" },
  too_many_rows_split_filters:  { label: "Too many rows", cls: "bg-rose-100 text-rose-700" },
  cancelled:                    { label: "Cancelled",  cls: "bg-slate-100 text-slate-700" },
};

export default function ExportsPage() {
  const [jobs, setJobs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [j, p, pr] = await Promise.all([
        api.get("/tenant/exports?limit=100"),
        api.get("/tenant/exports/presets"),
        api.get("/tenant/export-profiles"),
      ]);
      setJobs(j.data.items || []);
      setPresets(p.data.items || []);
      setProfiles(pr.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const download = async (job) => {
    try {
      const { data } = await api.get(`/tenant/exports/${job.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.artifact_filename || `export-${job.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const preflight = async (job) => {
    const { data } = await api.get(`/tenant/exports/${job.id}/preflight.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `preflight-${job.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="exports-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Administration
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Bulk exports</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Payroll-ready presets (ADP, Gusto, QuickBooks Online, Paychex) plus
            saveable custom profiles with the full column picker.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="exports-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button asChild data-testid="exports-new-profile">
            <Link to="/app/admin/exports/profiles/new"><FileSpreadsheet className="h-3.5 w-3.5" /> New profile</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Payroll presets</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {presets.map((p) => (
            <Link
              key={p.key} to={`/app/admin/exports/run?preset=${p.key}`}
              data-testid={`preset-tile-${p.key}`}
              className="card-elevate flex flex-col justify-between gap-2 rounded-lg border border-border bg-card p-3 hover:border-primary/40"
            >
              <div className="flex items-center gap-2 font-medium">
                <FileSpreadsheet className="h-4 w-4 text-secondary" /> {p.label}
              </div>
              <span className="inline-flex items-center gap-1 text-xs text-secondary">
                <Play className="h-3 w-3" /> Run — weekly {p.grain_default}
              </span>
            </Link>
          ))}
        </CardContent>
      </Card>

      {profiles.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Saved profiles ({profiles.length})</CardTitle>
            <Button asChild size="sm" variant="ghost"><Link to="/app/admin/exports/profiles">Manage</Link></Button>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {profiles.slice(0, 6).map((p) => (
              <div key={p.id} className="rounded-lg border border-border bg-card p-3" data-testid={`profile-card-${p.id}`}>
                <div className="font-medium">{p.name}</div>
                {p.description && <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>}
                <div className="mt-2 flex flex-wrap gap-1 text-[10px] text-muted-foreground">
                  <Badge variant="outline">{p.grain}</Badge>
                  <Badge variant="outline">{p.filetype}</Badge>
                  <Badge variant="outline">{p.columns?.length || 0} columns</Badge>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button asChild size="sm" data-testid={`profile-run-${p.id}`}>
                    <Link to={`/app/admin/exports/run?profile=${p.id}`}><Play className="h-3 w-3" /> Run</Link>
                  </Button>
                  <Button asChild size="sm" variant="ghost">
                    <Link to={`/app/admin/exports/profiles/${p.id}`}>Edit</Link>
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Recent jobs ({jobs.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kind</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </TableCell></TableRow>
              )}
              {!loading && jobs.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  No exports yet. Run a preset or a saved profile to see history here.
                </TableCell></TableRow>
              )}
              {jobs.map((j) => {
                const s = STATUS_BADGES[j.status] || { label: j.status, cls: "bg-slate-100" };
                return (
                  <TableRow key={j.id} data-testid={`export-row-${j.id}`}>
                    <TableCell className="font-medium">
                      {j.preset ? `Preset: ${j.preset.toUpperCase()}` : j.profile_id ? "Profile run" : "Ad-hoc"}
                      <div className="text-xs text-muted-foreground">{j.grain} · {j.filetype}</div>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {j.row_count}
                      {j.preflight_error_count > 0 && (
                        <Badge variant="outline" className="ml-2 text-amber-700" data-testid={`export-preflight-${j.id}`}>
                          {j.preflight_error_count} preflight
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`} data-testid={`export-status-${j.id}`}>
                        {s.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {j.started_at ? formatDistanceToNow(new Date(j.started_at), { addSuffix: true }) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {j.preflight_error_count > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => preflight(j)}>
                            <Download className="h-3 w-3" /> Preflight
                          </Button>
                        )}
                        {j.status === "ready" && (
                          <Button size="sm" onClick={() => download(j)} data-testid={`export-download-${j.id}`}>
                            <Download className="h-3 w-3" /> Download
                          </Button>
                        )}
                        {j.error_message && (
                          <span className="text-xs text-rose-700" title={j.error_message}>ⓘ</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
